import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { DemoDataService } from 'src/shared/services/demo-data.service';

// Rental strategy for the Optimizer page: which calendar segments are
// reserved for longer-term stays (blocking them from the short-term
// pipeline), and the monthly-rate targets those segments are optimized
// toward. Short-term vs longer-term is the only calendar-level distinction —
// the mid-vs-long split survives solely in targets/comps (mid_monthly_*
// vs long_monthly_*), never on a calendar window's tier. Mock-data service
// following the leasing convention — flip useDummyData to false once the
// real endpoints exist.

export type StayTier = 'short' | 'longer' | 'none';

export interface ReservedWindow {
  id: number;
  address_id: number;
  start_date: string;       // ISO date, inclusive
  end_date: string | null;  // ISO date, inclusive; null = open-ended
  tier: StayTier;
  note?: string;
}

// Amounts are in cents, matching PricingCalendarDay.price.
export interface StrategyTargets {
  mid_monthly_target: number;
  mid_monthly_floor: number;
  long_monthly_target: number;
  long_monthly_floor: number;
  long_min_lease_months: number;
  short_term_enabled: boolean;
  longer_term_enabled: boolean;
}

export const TIER_LABELS: Record<StayTier, string> = {
  short: 'Short-term',
  longer: 'Longer-term',
  none: 'Blocked',
};

export function findReservedWindow(date: string, windows: ReservedWindow[]): ReservedWindow | null {
  return windows.find(w => date >= w.start_date && (w.end_date === null || date <= w.end_date)) ?? null;
}

// tidy-datepicker writes a JS Date into its form control, but windows store
// ISO date strings (findReservedWindow compares lexicographically) — always
// normalize before persisting.
export function toIsoDate(value: string | Date): string {
  if (value instanceof Date) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
  }
  return value;
}

// Local (not UTC) day-math helpers so DST/timezone offsets never shift the
// ISO date string by a day — used only for clipping windows around a range.
function dateFromIso(date: string): Date {
  return new Date(`${date}T00:00:00`);
}

function prevDay(date: string): string {
  const d = dateFromIso(date);
  d.setDate(d.getDate() - 1);
  return toIsoDate(d);
}

function nextDay(date: string): string {
  const d = dateFromIso(date);
  d.setDate(d.getDate() + 1);
  return toIsoDate(d);
}

let nextWindowId = 100;

const DUMMY_WINDOWS: ReservedWindow[] = [
  { id: 1, address_id: 990001, start_date: '2026-09-01', end_date: '2026-11-30', tier: 'longer', note: 'Off-season — open to traveling-nurse and relocation stays' },
  { id: 2, address_id: 990003, start_date: '2026-07-01', end_date: null, tier: 'longer', note: 'Entire calendar reserved for a long-term lease' },
  { id: 3, address_id: 990001, start_date: '2026-08-21', end_date: '2026-08-31', tier: 'short', note: 'Festival weekends — hold for premium nightly rates' },
];

const DEFAULT_TARGETS: StrategyTargets = {
  mid_monthly_target: 420000,
  mid_monthly_floor: 360000,
  long_monthly_target: 245000,
  long_monthly_floor: 225000,
  long_min_lease_months: 12,
  short_term_enabled: true,
  longer_term_enabled: true,
};

// Cadence Cottage's entire calendar is reserved for a long-term lease, so
// short-term optimization is off by default for that listing.
const DUMMY_TARGETS: Record<number, StrategyTargets> = {
  990003: { ...DEFAULT_TARGETS, short_term_enabled: false },
};

@Injectable({ providedIn: 'root' })
export class RentalStrategyService {
  private http = inject(HttpClient);
  private demoData = inject(DemoDataService);
  private useDummyData = true;

  // Reserved windows have no backend yet — the sample windows belong only to the
  // demo/preview worlds; real accounts must never see fabricated holds.
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  // Emits after any strategy mutation (windows or targets) so calendar
  // surfaces can refresh without coupling to how the mutating view (day
  // panel, inline form, settings toggle) was presented — the panel-close
  // signal proved unreliable across desktop/mobile dialogs.
  readonly strategyChanged$ = new Subject<void>();

  async getReservedWindows(addressId: number): Promise<ReservedWindow[]> {
    if (this.useDummyData) {
      return this.showsDummyData ? DUMMY_WINDOWS.filter(w => w.address_id === addressId) : [];
    }
    return firstValueFrom(this.http.get<ReservedWindow[]>(`${environment.api_url}api/v1/customer/rental-strategy/${addressId}/reserved-windows`));
  }

  async addReservedWindow(addressId: number, data: { start_date: string; end_date: string | null; tier: StayTier; note?: string }): Promise<ReservedWindow> {
    if (this.useDummyData) {
      const reservedWindow: ReservedWindow = { id: nextWindowId++, address_id: addressId, ...data };
      DUMMY_WINDOWS.push(reservedWindow);
      this.strategyChanged$.next();
      return reservedWindow;
    }
    return firstValueFrom(this.http.post<ReservedWindow>(`${environment.api_url}api/v1/customer/rental-strategy/${addressId}/reserved-windows`, data));
  }

  async removeReservedWindow(addressId: number, windowId: number): Promise<void> {
    if (this.useDummyData) {
      const index = DUMMY_WINDOWS.findIndex(w => w.address_id === addressId && w.id === windowId);
      if (index !== -1) {
        DUMMY_WINDOWS.splice(index, 1);
        this.strategyChanged$.next();
      }
      return;
    }
    await firstValueFrom(this.http.delete(`${environment.api_url}api/v1/customer/rental-strategy/${addressId}/reserved-windows/${windowId}`));
  }

  async getTargets(addressId: number): Promise<StrategyTargets> {
    if (this.useDummyData) {
      return DUMMY_TARGETS[addressId] ?? { ...DEFAULT_TARGETS };
    }
    return firstValueFrom(this.http.get<StrategyTargets>(`${environment.api_url}api/v1/customer/rental-strategy/${addressId}/targets`));
  }

  async updateTargets(addressId: number, changes: Partial<StrategyTargets>): Promise<StrategyTargets> {
    if (this.useDummyData) {
      DUMMY_TARGETS[addressId] = { ...(DUMMY_TARGETS[addressId] ?? DEFAULT_TARGETS), ...changes };
      this.strategyChanged$.next();
      return DUMMY_TARGETS[addressId];
    }
    return firstValueFrom(this.http.put<StrategyTargets>(`${environment.api_url}api/v1/customer/rental-strategy/${addressId}/targets`, changes));
  }

  // Sets availability for [start..end] (inclusive; start === end for a single
  // date). Any existing window overlapping the range is clipped so it no
  // longer covers those dates, then — unless the new state is fully
  // available (both tiers on, i.e. no window needed) — one new window is
  // written over the range with a tier derived from the requested state.
  async setAvailability(addressId: number, start: string, end: string, state: { short: boolean; longer: boolean }): Promise<void> {
    if (this.useDummyData) {
      for (let i = DUMMY_WINDOWS.length - 1; i >= 0; i--) {
        const w = DUMMY_WINDOWS[i];
        if (w.address_id !== addressId) {continue;}
        const overlaps = start <= (w.end_date ?? '9999-12-31') && (end >= w.start_date);
        if (!overlaps) {continue;}

        const pieces: ReservedWindow[] = [];
        if (w.start_date < start) {
          pieces.push({ ...w, end_date: prevDay(start) });
        }
        if (w.end_date === null || w.end_date > end) {
          pieces.push({ ...w, id: nextWindowId++, start_date: nextDay(end), end_date: w.end_date });
        }
        DUMMY_WINDOWS.splice(i, 1, ...pieces);
      }

      const fullyAvailable = state.short && state.longer;
      if (!fullyAvailable) {
        const tier: StayTier = state.short && !state.longer ? 'short' : !state.short && state.longer ? 'longer' : 'none';
        DUMMY_WINDOWS.push({ id: nextWindowId++, address_id: addressId, start_date: start, end_date: end, tier });
      }
      this.strategyChanged$.next();
      return;
    }
    await firstValueFrom(this.http.put(`${environment.api_url}api/v1/customer/rental-strategy/${addressId}/availability`, { start_date: start, end_date: end, ...state }));
  }
}
